import type { ReactNode } from "react";
import { IconAlertCircle } from "@tabler/icons-react";
import { LockIcon } from "lucide-react";

// Shared form primitives for the roster dialogs. They live outside either
// dialog so add and edit present the same field, the same way.

// Matches the live input's geometry so the column keeps its rhythm, but states
// "not a control" three ways: a recessed fill that differs from the input in
// BOTH themes, a dashed edge, and a lock glyph.
//
// Deliberately NOT composed over styles.wizardInputCls — that string ends in
// `dark:bg-input`, and an unprefixed background appended to it survives
// tailwind-merge (different variant) only to lose to `dark:` at render, making
// a locked field identical to an editable one in dark mode.
export const readOnlyFieldCls =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-dashed border-border bg-muted px-3 text-sm text-text-primary dark:bg-muted";

export function ReadOnlyValue({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return (
    <output id={id} className={readOnlyFieldCls}>
      <span className="truncate">{children}</span>
      <LockIcon className="size-3 shrink-0 text-text-muted" aria-hidden />
    </output>
  );
}

// This dialog had grown four different label treatments (wizrdLabelCls at two
// sizes, a bare poppins/text-xs pair, and an uppercase eyebrow). Field and
// FieldLabel are the single treatment, matching DESIGN.md's Label style —
// Poppins Medium 0.75rem, tight tracking, not the wide tracking wizrdLabelCls
// carries for its own section headings.
export const fieldLabelCls =
  "poppins text-xs font-medium tracking-tight text-text-primary";

export function FieldLabel({
  htmlFor,
  children,
  required,
  optional,
}: {
  htmlFor?: string;
  children: ReactNode;
  required?: boolean;
  optional?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className={fieldLabelCls}>
      {children}
      {required && <span className="text-destructive ml-0.5">*</span>}
      {optional && (
        <span className="ml-1 font-normal text-text-muted">(optional)</span>
      )}
    </label>
  );
}

// One spacing rhythm for every field: 8px binds the label to its control, 4px
// hugs hint/error text under it, and the parent stack's 16px separates fields.
// Tight group, generous separation — the ratio is what makes the column read.
export function Field({
  children,
  hint,
  error,
  status,
}: {
  children: ReactNode;
  hint?: string;
  error?: string;
  /** Own footer content (e.g. a live availability check) in the hint slot. */
  status?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-2">{children}</div>
      {status}
      {error ? (
        <p className="flex items-center gap-1 text-xs text-destructive">
          <IconAlertCircle className="size-3 shrink-0" />
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-text-muted">{hint}</p>
      )}
    </div>
  );
}

// Form-level failures (the server's own message) read as a block, not as a
// field hint: muted destructive fill per DESIGN.md, never a solid red alarm.
export function ApiError({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2.5 text-xs leading-relaxed text-destructive whitespace-pre-line motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
    >
      <IconAlertCircle className="size-4 shrink-0 mt-px" />
      {message}
    </p>
  );
}
